import { type FC, memo, useEffect, useState } from 'react';
import {
  allowLeave,
  closeDialogue,
  leaveDialogue,
  openDialogue,
  startSave,
} from './actions';

import { useRouteTransitionBlockerLegacy } from '@ipfabric/ui-components/LeaveDialog/useRouteTransitionBlocker';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { Dialog } from 'components/Dialog/Dialog';
import type { Location, Transition } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectLeaveDialogue } from './selectors';

export type SaveEvent = Omit<
  Location & {
    closeDialogue: () => void;
  },
  'key'
>;

interface Props {
  isPristine: boolean;
  isValid?: boolean;
  msg: {
    header: string;
    subHeader: string;
    btnSave: string;
    btnCancel?: string;
    btnLeave: string;
  };
  onCancel?: () => void;
  onOpen?: () => void;
  onLeave?: () => void;
  onSave: (args: SaveEvent) => void;
  saveChangeOnLeave?: boolean;
  saving?: boolean;
}

/**
 * LeaveDialog featuring prevention of native browser dialog, saving directly from the dialog
 * (troublesome to handle in all cases, often broken even happy path), using routeTransitionBlockerLegacy
 * featuring some canLeave logic on location state object.
 *
 * @deprecated use simple, intuitive and documented ui-components/LeaveDialog instead.
 */
const BareLeaveDialog: FC<Props> = (props) => {
  const dialog = useSelector<
    unknown,
    // Immutable.Record
    {
      pathname: string;
      search: string;
      state: any;
      hash: string;
      canLeave: boolean;
      open: boolean;
      disabled: boolean;
    }
  >(makeSelectLeaveDialogue());
  const [transition, setTransition] = useState<Transition | null>(null);
  const dispatch = useDispatch();

  const closeDialog = useStableCallback(() => {
    dispatch(closeDialogue());
  });

  const handleSave = () => {
    dispatch(startSave());
    props.onSave({
      pathname: dialog.pathname,
      search: dialog.search,
      state: dialog.state,
      hash: dialog.hash,
      closeDialogue: closeDialog,
    });
    // useRouteTransitionBlocker blocks the transition while canLeave is false
    dispatch(allowLeave());
  };

  const handleCancel = () => {
    props.onCancel?.();
    closeDialog();
  };

  const handleLeave = () => {
    dispatch(leaveDialogue());
    props.onLeave?.();
    transition?.retry();
    closeDialog();
  };

  const handleBlock = useStableCallback((tx: Transition) => {
    const state = tx.location.state as
      | {
          canLeave?: boolean;
          validate?: boolean;
        }
      | undefined;
    const openDialog = () => {
      dispatch(
        openDialogue({
          pathname: tx.location.pathname,
          search: tx.location.search,
          state: tx.location.state,
          hash: tx.location.hash,
        }),
      );
      props.onOpen?.();
    };
    const canLeave = dialog.canLeave || state?.canLeave;
    if (props.isPristine || canLeave) {
      if (dialog.open) {
        closeDialog();
      }
      tx.retry();
      return;
    }
    if (props.saveChangeOnLeave && props.isValid) {
      props.onSave({
        pathname: tx.location.pathname,
        search: tx.location.search,
        state: tx.location.state,
        hash: tx.location.hash,
        closeDialogue: closeDialog,
      });
    } else {
      openDialog();
    }
    setTransition(tx);
  });

  const preventRouteLeave = !props.isPristine && !dialog.canLeave;
  useRouteTransitionBlockerLegacy(handleBlock, preventRouteLeave);

  useEffect(() => {
    return () => {
      closeDialog();
    };
  }, []);

  if (!dialog.open) {
    return null;
  }

  return (
    <Dialog
      headerMsg={props.msg.header}
      subHeaderMsg={props.msg.subHeader}
      primaryBtnMsg={
        <>
          {props.saving ? (
            <i className='fa-regular fa-spinner fa-spin fa-fw mr-lgu-2' />
          ) : null}
          {props.isValid
            ? props.msg.btnSave
            : props.msg.btnCancel || props.msg.btnSave}
        </>
      }
      secondaryBtnMsg={props.msg.btnLeave}
      onClose={handleCancel}
      onPrimaryClick={props.isValid ? handleSave : handleCancel}
      onSecondaryClick={handleLeave}
      primaryDisabled={dialog.disabled || props.saving}
      secondaryDisabled={dialog.disabled || props.saving}
      primaryBtnVariant='secondary'
      primaryBtnAccent='positive'
      secondaryBtnVariant='primary'
      secondaryBtnAccent='negative'
    />
  );
};

export const LeaveDialog = memo(BareLeaveDialog);
